import { useEffect, useState } from 'react';
import { fetchArcWeldingFormOptions, fetchArcWeldingSites } from '../services/arcWelding';
import {
  AW_EQUIPMENT_TYPES,
  AW_GRADES,
  AW_METHODS,
  AW_PARAMETERS,
  AW_VALUE_CLASSIFICATIONS,
  AW_WFS_UNITS,
  CERTIFICATE_CELL_COLORS,
  PROCEDURAL_CONFIRMATION_FIELDS,
} from '../utils/arcWeldingVerificationUi';

let cachedOptions = null;
let loadPromise = null;

/** Clear cached form-options (e.g. after deploy) so labels/enums reload. */
export const resetArcWeldingFormOptionsCache = () => {
  cachedOptions = null;
  loadPromise = null;
};

const asArray = (value, fallback = []) => (Array.isArray(value) && value.length ? value : fallback);

/** BE may return string enums or { key, label } / { value, label } objects. */
export const normalizeOptionEntry = (item) => {
  if (item == null) return '';
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  if (typeof item === 'object') {
    return item.label ?? item.value ?? item.key ?? item.name ?? '';
  }
  return String(item);
};

const normalizeStringList = (value, fallback = []) => {
  const list = asArray(value, fallback);
  return list.map(normalizeOptionEntry).filter(Boolean);
};

/** Preserve { key, label } from BE; fall back to PROCEDURAL_CONFIRMATION_FIELDS labels. */
export const normalizeProceduralConfirmationFields = (value, fallback = PROCEDURAL_CONFIRMATION_FIELDS) => {
  const list = asArray(value, fallback);
  const fallbackByKey = Object.fromEntries(fallback.map((f) => [f.key, f.label]));

  return list
    .map((entry) => {
      if (typeof entry === 'string') {
        return { key: entry, label: fallbackByKey[entry] ?? entry };
      }
      if (entry && typeof entry === 'object') {
        const key = entry.key ?? entry.value ?? '';
        const label = fallbackByKey[key] ?? entry.label ?? key;
        return key ? { key, label } : null;
      }
      return null;
    })
    .filter(Boolean);
};

const normalizeKeyList = (value, fallback = []) =>
  normalizeProceduralConfirmationFields(value, fallback).map((f) => f.key);

export const normalizeArcWeldingFormOptions = (payload = {}) => ({
  valueClassification: normalizeStringList(payload.valueClassification, AW_VALUE_CLASSIFICATIONS),
  grade: normalizeStringList(payload.grade, AW_GRADES),
  verificationRange: normalizeStringList(payload.verificationRange, []),
  process: normalizeStringList(payload.process, []),
  outputMode: normalizeStringList(payload.outputMode, []),
  wfsUnit: normalizeStringList(payload.wfsUnit, AW_WFS_UNITS),
  equipmentType: normalizeStringList(payload.equipmentType, AW_EQUIPMENT_TYPES),
  parameter: normalizeStringList(payload.parameter, AW_PARAMETERS),
  method: normalizeStringList(payload.method, Object.values(AW_METHODS)),
  toleranceType: normalizeStringList(payload.toleranceType, ['percent', 'absolute']),
  pointResult: normalizeStringList(payload.pointResult, ['Pass', 'Fail', 'Incomplete']),
  status: normalizeStringList(payload.status, []),
  overallResult: normalizeStringList(payload.overallResult, []),
  sites: asArray(payload.sites ?? payload.rows, []),
  cellColors: payload.cellColors ?? CERTIFICATE_CELL_COLORS,
  proceduralConfirmationKeys: normalizeKeyList(
    payload.proceduralConfirmationKeys,
    PROCEDURAL_CONFIRMATION_FIELDS
  ),
  proceduralConfirmationFields: normalizeProceduralConfirmationFields(
    payload.proceduralConfirmationKeys,
    PROCEDURAL_CONFIRMATION_FIELDS
  ),
});

export const getArcWeldingFormOptions = async () => {
  if (cachedOptions) return cachedOptions;
  if (!loadPromise) {
    loadPromise = fetchArcWeldingFormOptions()
      .then(async (data) => {
        let sites = data?.sites ?? data?.rows;
        if (!asArray(sites, []).length) {
          try {
            const legacySites = await fetchArcWeldingSites();
            sites = legacySites?.rows ?? legacySites;
          } catch {
            sites = [];
          }
        }
        cachedOptions = normalizeArcWeldingFormOptions({ ...data, sites });
        return cachedOptions;
      })
      .catch((err) => {
        loadPromise = null;
        throw err;
      });
  }
  return loadPromise;
};

export const useArcWeldingFormOptions = () => {
  const [options, setOptions] = useState(cachedOptions);
  const [loading, setLoading] = useState(!cachedOptions);

  useEffect(() => {
    let mounted = true;
    getArcWeldingFormOptions()
      .then((data) => {
        if (mounted) setOptions(data);
      })
      .catch(() => {
        if (mounted) setOptions(normalizeArcWeldingFormOptions({}));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return { options: options ?? normalizeArcWeldingFormOptions({}), loading };
};

export const getProceduralConfirmationFields = (options) => {
  const fallbackByKey = Object.fromEntries(
    PROCEDURAL_CONFIRMATION_FIELDS.map((f) => [f.key, f.label])
  );
  const raw = options?.proceduralConfirmationFields?.length
    ? options.proceduralConfirmationFields
    : options?.proceduralConfirmationKeys?.length
      ? options.proceduralConfirmationKeys.map((key) => ({ key, label: fallbackByKey[key] ?? key }))
      : PROCEDURAL_CONFIRMATION_FIELDS;

  return raw.map((field) => {
    const key = typeof field === 'string' ? field : field.key;
    const label = fallbackByKey[key]
      ?? (typeof field === 'object' && typeof field.label === 'string'
        ? field.label
        : normalizeOptionEntry(field?.label ?? key));
    return { key, label };
  });
};
